import { Store } from "pullstate";
import {
  ModalState as EnvModalState,
  SoftwareEnvironment,
} from "../domain/softwareEnvironments";
import { ApiError } from "../apiUtils";
import {
  ClustersState,
  ClusterWsConnector,
  Column,
} from "../pages/Clusters/types";
import { Invoice } from "../domain/billing";
import { loadStoredColumns } from "../shared-components/columnsCrud";
import {
  BILLING_COLUMN_KEY,
  BillingColumn,
  ALL_COLUMNS as ALL_BILLING_COLUMNS,
  DEFAULT_COLUMNS as DEFAULT_BILLING_COLUMNS,
} from "../pages/Staff/crud";
import { AccountPublic, AccountType } from "../crud/account/types";
import { MemberWorkspaceSchema } from "../crud/account/fetch";

export type LoadingType = { type: "loading" };
export type LoadErrorType<T> = {
  type: "error";
  error: ApiError | string;
  data?: T;
};
export type LoadedType<T> = { type: "loaded"; data: T };

export type NoneType = { type: "none" };

export type LoadState<T> =
  | LoadingType
  | LoadErrorType<T>
  | LoadedType<T>
  | NoneType;

export type LoadStateNotNone<T> =
  | LoadingType
  | LoadErrorType<T>
  | LoadedType<T>;

export type DeleteState =
  | { type: "none" }
  | { type: "waitingOnUser"; id: number }
  | { type: "waitingOnServer"; id: number }
  | { type: "error"; id: number; error: string };

// Putting these together because they depend on
// each other, and I want to have only one loadState
// for them.
export type EnvsAndConfigs = {
  softwareEnvironments: SoftwareEnvironment[];
};

export type BillingData = {
  invoices: Invoice[];
  priceIds: {
    paygId: string;
  };
};

export type AppState = {
  loggingIn: boolean;
  viewedAccount: {
    loadState: LoadState<AccountPublic | MemberWorkspaceSchema>;
  };
  envsAndConfigs: {
    loadState: LoadState<EnvsAndConfigs>;
  };
  softwareEnvironments: {
    modalState: EnvModalState;
    deleteState: DeleteState;
    tabNumber: number;
    quickstartOpen: boolean;
  };
  clusters: {
    connector?: ClusterWsConnector;
    loadState: ClustersState;
    quickstartOpen: boolean;
    usageOpen: boolean;
    columns: Column[];
  };
  billing: {
    loadState: LoadState<BillingData>;
  };
  // coiled-admin only
  billingDashboard: {
    columns: BillingColumn[];
  };
};

export const UIStore = new Store<AppState>({
  loggingIn: false,
  viewedAccount: {
    loadState: {
      type: "none",
    },
  },
  envsAndConfigs: {
    loadState: { type: "none" },
  },
  softwareEnvironments: {
    modalState: { type: "none", environment: undefined },
    deleteState: { type: "none" },
    tabNumber: 0,
    quickstartOpen: true,
  },
  clusters: {
    connector: undefined,
    loadState: {
      type: "loading",
      clusters: [],
    },
    quickstartOpen: true,
    usageOpen: true,
    columns: [],
  },
  billing: {
    loadState: { type: "none" },
  },
  billingDashboard: {
    columns: loadStoredColumns(
      BILLING_COLUMN_KEY,
      DEFAULT_BILLING_COLUMNS,
      ALL_BILLING_COLUMNS,
    ),
  },
});

UIStore.createReaction(
  (s) => s.viewedAccount.loadState,
  (viewedAccountState, draft, original, previousState) => {
    if (
      previousState.type === "loaded" &&
      viewedAccountState.type === "loaded" &&
      previousState.data.slug !== viewedAccountState.data?.slug
    ) {
      // reset account-scoped state.
      draft.softwareEnvironments = {
        modalState: { type: "none", environment: undefined },
        deleteState: { type: "none" },
        tabNumber: 0,
        quickstartOpen: true,
      };
      draft.billing = {
        loadState: { type: "none" },
      };
    }
  },
);

// Start up a clusters websocket whenever viewedAccount changes
UIStore.createReaction(
  (s) => s.viewedAccount.loadState,
  (viewedAccountState, draft, original) => {
    if (viewedAccountState.type !== "loaded") {
      return;
    }
    if (!(viewedAccountState.data.type === AccountType.member)) {
      return;
    }
  },
);
